import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from 'reactstrap';
import { debounce } from 'lodash-es';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '@s20/i18n';
import { httpClient } from '@s20/services/httpClient';
import { Alert } from '../../Modal';
import {
    FlexBetweenRow, FlexEndRow, FormLabel, StyledButton,
} from '../FormElements';
import { ApiRoutes } from '../../../Common';

export const SignatureModal = ({
    id, onSigSavedCallback, sigData, reviewId,
}) => {
    const { t } = useTranslation();

    const [isValid, setIsValid] = useState(false);
    const [isValidated, setIsValidated] = useState(false);

    const signaturePadRef = useRef(null);
    const signaturePadContainerRef = useRef(null);

    // When resizing, we need to save the image contents to a fake canvas and then restore them
    const fakeCanvasRef = useRef(null);

    const onClear = useCallback(() => {
        signaturePadRef.current.clear();
    }, []);

    const onCancel = useCallback(() => {
        onClear();

        Alert.hide();
    }, [onClear]);

    const onSaveSignature = useCallback(() => {
        if (!signaturePadRef.current.isEmpty()) {
            onSigSavedCallback(signaturePadRef.current.toDataURL('image/png'));
            setIsValid(true);
            setIsValidated(true);

            Alert.hide();
        } else {
            setIsValid(false);
            setIsValidated(true);
        }
    }, [onSigSavedCallback]);

    const handleResizeCanvas = useCallback(() => {
        const canvas = signaturePadRef.current.getCanvas();
        const ctx = canvas.getContext('2d');

        const needsRedraw = !signaturePadRef.current.isEmpty();

        // Resizing the viewport causes the canvas to empty
        // We copy the canvas contents to a fake one and then copy it back after resizing
        // https://github.com/szimek/signature_pad#handling-canvas-resize
        if (needsRedraw) {
            const fakeCanvas = fakeCanvasRef.current;
            fakeCanvas.height = canvas.height;
            fakeCanvas.width = canvas.width;

            const fakeCtx = fakeCanvas.getContext('2d');
            fakeCtx.drawImage(canvas, 0, 0);
        }

        // We have to 0 the height and width first otherwise some weird stuff happens
        canvas.height = 0;
        canvas.width = 0;

        const { height, width } = signaturePadContainerRef.current.getBoundingClientRect();

        canvas.height = height;
        canvas.width = width;

        if (needsRedraw) {
            const fakeCanvas = fakeCanvasRef.current;

            ctx.drawImage(
                fakeCanvas,
                0,
                0,
                fakeCanvas.width,
                fakeCanvas.height, // source rectangle
                0,
                0,
                canvas.width,
                canvas.height,
            ); // destination rectangle
        }
    }, []);

    useEffect(() => {
        if (!fakeCanvasRef.current) {
            fakeCanvasRef.current = document.createElement('canvas');
        }

        const loadSignature = async () => {
            if (!sigData) return;

            if (sigData.startsWith('data:')) {
                // Same-origin data URI (legacy byte[]-backed signatures) — safe to load directly.
                signaturePadRef.current.fromDataURL(sigData);
                return;
            }

            // Remote (SAS) URL — drawing this directly would taint the canvas (cross-origin content
            // with no CORS permission), breaking every later pixel read (isEmpty, save) with a
            // SecurityError. Fetch the bytes through our own API instead, same-origin, and load the
            // resulting data URI onto the pad.
            try {
                const { data } = await httpClient.js(ApiRoutes.timesheetReview.signatureImage(reviewId));
                signaturePadRef.current.fromDataURL(`data:image/png;base64,${data}`);
            } catch (error) {
                console.error('Failed to load existing signature image for editing:', error);
            }
        };

        loadSignature();

        const debouncedHandler = debounce(handleResizeCanvas, 200);
        debouncedHandler();

        window.addEventListener('resize', debouncedHandler);

        return () => {
            window.removeEventListener('resize', debouncedHandler);
            debouncedHandler.cancel();
        };
    }, [handleResizeCanvas, sigData, reviewId]);

    return (
        <div>
            <div>
                <FormLabel
                    for={id}
                    label={t('timesheet:signatureColon')}
                    required
                />
                <div
                    id={id}
                    className={`signature-container ${isValidated && !isValid ? 'border border-danger' : 'border border-dark'}`}
                    ref={signaturePadContainerRef}
                >
                    <SignatureCanvas
                        clearOnResize={false}
                        ref={signaturePadRef}
                        canvasProps={{ clearOnResize: false }}
                    />
                </div>
            </div>

            <FlexBetweenRow>
                <div>
                    {!!isValidated && !isValid && <small className="text-danger">{t('timesheet:signatureRequired')}</small>}
                </div>

                <FlexEndRow className="mt-4">
                    <Button
                        type="button"
                        color="link"
                        className="mr-2"
                        onClick={onCancel}
                    >
                        {t('common:cancel')}
                    </Button>

                    <StyledButton
                        color="secondary"
                        className="mr-2"
                        onClick={onClear}
                        outlined
                    >
                        {t('common:clear')}
                    </StyledButton>

                    <StyledButton
                        color="primary"
                        onClick={onSaveSignature}
                        startIcon={faSignature}
                    >
                        {t('common:save')}
                    </StyledButton>
                </FlexEndRow>
            </FlexBetweenRow>
        </div>
    );
};